import React, { useState, useEffect } from 'react'
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import { grey } from '@mui/material/colors';
import DeleteIcon from "@mui/icons-material/Delete";
import Link from "@mui/material/Link";
import { useNavigate } from 'react-router-dom';
import { getLists, deleteList, getSharedLists, updateList } from '../../services/userService';
import { Chip, Tabs, Tab, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const UserLists = () => {
  const [lists, setLists] = useState([]);
  const [sharedLists, setSharedLists] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [loadingShared, setLoadingShared] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editNuggets, setEditNuggets] = useState('');
  const [updateLoading, setUpdateLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLists = async () => {
      try {
        const response = await getLists();
        if (response && response.data) {
          setLists(response.data);
        } else if (Array.isArray(response)) {
          // Handle case where response is directly an array
          setLists(response);
        } else {
          setLists([]);
        }
      } catch (error) {
        console.error('Failed to fetch lists:', error);
        // Error is handled by API interceptor for 401s
        // For other errors, set empty array
        if (error.response && error.response.status !== 401) {
          setLists([]);
        }
      }
    };
    fetchLists();
  }, []);

  const handleDeleteList = async (listId) => {
    const response = await deleteList(listId);
    if (response.status === 'success') {
      // Remove from appropriate list based on current tab
      if (tabValue === 0) {
        setLists(lists.filter((list) => list._id !== listId));
      } else {
        setSharedLists(sharedLists.filter((list) => list._id !== listId));
      }
    }
  };

  const handleEditList = (list) => {
    setEditingList(list);
    setEditName(list.name || '');
    setEditDescription(list.description || '');
    setEditNuggets(list.nuggets || '');
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditingList(null);
    setEditName('');
    setEditDescription('');
    setEditNuggets('');
  };

  const handleUpdateList = async () => {
    if (!editingList || !editName.trim()) {
      alert('Please enter a list name');
      return;
    }

    try {
      setUpdateLoading(true);
      const response = await updateList(editingList._id, {
        name: editName.trim(),
        description: editDescription.trim(),
        nuggets: editNuggets.trim()
      });

      if (response.status === 'success') {
        // Update the list in the appropriate array
        if (tabValue === 0) {
          setLists(lists.map(list => 
            list._id === editingList._id 
              ? { ...list, name: editName.trim(), description: editDescription.trim(), nuggets: editNuggets.trim() }
              : list
          ));
        } else {
          setSharedLists(sharedLists.map(list => 
            list._id === editingList._id 
              ? { ...list, name: editName.trim(), description: editDescription.trim(), nuggets: editNuggets.trim() }
              : list
          ));
        }
        handleEditDialogClose();
      } else {
        alert('Failed to update list. Please try again.');
      }
    } catch (error) {
      console.error('Error updating list:', error);
      alert('Failed to update list. Please try again.');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleListClick = (listId) => {
    // Navigate to shared list route if on Shared Lists tab, otherwise regular list route
    if (tabValue === 1) {
      navigate(`/shared-list/${listId}`);
    } else {
      navigate(`/list/${listId}`);
    }
  };

  const handleTabChange = async (event, newValue) => {
    setTabValue(newValue);
    
    // Fetch shared lists when switching to Shared Lists tab
    if (newValue === 1 && sharedLists.length === 0 && !loadingShared) {
      setLoadingShared(true);
      try {
        const response = await getSharedLists();
        if (response && response.data) {
          setSharedLists(response.data);
        } else if (Array.isArray(response)) {
          // Handle case where response is directly an array
          setSharedLists(response);
        } else {
          setSharedLists([]);
        }
      } catch (error) {
        console.error('Failed to fetch shared lists:', error);
        // Error is handled by API interceptor for 401s
        // For other errors, set empty array
        if (error.response && error.response.status !== 401) {
          setSharedLists([]);
        }
      } finally {
        setLoadingShared(false);
      }
    }
  };

  // Use appropriate list based on selected tab
  const filteredLists = tabValue === 0 ? lists : sharedLists;

  return (
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', m:2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#0a1128', borderRadius: 1, boxShadow: 2, p: 2  , mb:2, width: '100%' }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#eff7f6', mb: 0 }}>Lists</Typography>
      </Box>
      
      <Box sx={{ width: '100%', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="My Lists" />
          <Tab label="Shared Lists" />
        </Tabs>
      </Box>
      
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: grey[200] }}>
              <TableCell sx={{ fontWeight: 600 }}>S.No</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>List Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Nuggets</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Total Profiles</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingShared && tabValue === 1 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredLists.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {tabValue === 0 ? 'No lists found. Create your first list!' : 'No shared lists found.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredLists.map((list, index) => (
              <TableRow key={list._id} sx={{ '&:hover': { backgroundColor: grey[100] } }}>
                <TableCell sx={{ fontWeight: 600 }}>{index + 1}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <Link
                    component="button"
                    onClick={() => handleListClick(list._id)}
                    
                  >
                   <Chip sx={{ fontWeight: 600, bgcolor: '#1BE885' }} label={list.name} />
                  </Link>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {list.description 
                    ? (() => {
                        const words = list.description.split(' ');
                        return words.length > 10 ? words.slice(0, 10).join(' ') + '...' : list.description;
                      })()
                    : 'No description'
                  }
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {list.nuggets
                    ? (() => {
                        const words = String(list.nuggets).split(' ');
                        return words.length > 5 ? words.slice(0, 5).join(' ') + '...' : String(list.nuggets);
                      })()
                    : '—'
                  }
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {list.numberOfProfiles !== undefined 
                    ? list.numberOfProfiles 
                    : (list.linkedinUsernames ? list.linkedinUsernames.length : 0)
                  }
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <IconButton 
                    size="small" 
                    sx={{ color: '#1976d2', mr: 1 }} 
                    onClick={() => handleEditList(list)}
                    disabled={tabValue === 1 || !list.isOwner}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    sx={{ color: '#d32f2f' }} 
                    onClick={() => handleDeleteList(list._id)}
                    disabled={tabValue === 1 || !list.isOwner}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit List Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Edit List
          <IconButton
            aria-label="close"
            onClick={handleEditDialogClose}
            sx={(theme) => ({
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="List Name"
            type="text"
            fullWidth
            variant="outlined"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Nuggets"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={editNuggets}
            onChange={(e) => setEditNuggets(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleEditDialogClose} disabled={updateLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateList} 
            variant="contained"
            disabled={updateLoading || !editName.trim()}
            sx={{
              bgcolor: '#1BE885',
              '&:hover': { bgcolor: '#17d077' }
            }}
          >
            {updateLoading ? <CircularProgress size={20} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default UserLists
